'use client';

import { css, keyframes } from 'styled-components';

import { darkHomeTokens } from './tokens';

/**
 * Named fragments shared by two or more components. The surface is one
 * continuous dark canvas whose one recurring treatment is the soft-border
 * panel (bento cells, demo stage), plus the accent ramp, the stand-up
 * entrance, and the cards' cursor spotlight.
 */

/**
 * The support probe every scroll-driven animation sits behind. The
 * `animation-range` clause is load-bearing: an engine with partial support —
 * timeline without ranges — passes a bare `animation-timeline` test, drops the
 * range declarations, and runs the animation over the wrong stretch of scroll
 * (for the copy float below, the stagger IS its ranges). Probing both keeps
 * such engines on the static arrived state instead.
 */
export const sdaSupports =
  '@supports ((animation-timeline: view()) and (animation-range: entry))';

/**
 * The blue→purple→pink accent gradient the hero paints onto the “growth”
 * headline word. Shared so the other accents on the page (the proof numbers,
 * the closing CTA’s emphasis) read as the same treatment rather than a
 * near-duplicate. Applies itself as a clipped text fill, so put it on the
 * inline element wrapping the text you want tinted.
 *
 * The outer stops are DVD’s own blue300 and pink400; only the purple middle
 * stop is this surface’s.
 */
export const accentGradient = css`
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.colors.blue300},
    ${darkHomeTokens.colors.accentPurple} 55%,
    ${({ theme }) => theme.colors.pink400}
  );
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

/**
 * The cursor spotlight's paint layer: a soft radial highlight pinned to the
 * `--spot-x`/`--spot-y` the `moveSpotlight` handler (../_lib/move-spotlight)
 * publishes, lifted from the Wistia AI band (#1362). Hidden at rest; each
 * consumer writes its own reveal rule (hover, focus, active), since what
 * counts as lit differs per card. The host needs `position: relative` and its
 * own stacking context (`isolation: isolate`) so the negative z-index sits
 * above the card's background but below its content.
 */
export const spotlightLayer = css`
  &::before {
    background: radial-gradient(
      10rem circle at var(--spot-x, 50%) var(--spot-y, 50%),
      ${darkHomeTokens.colors.accentWash},
      transparent 70%
    );
    content: '';
    inset: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    transition: opacity ${({ theme }) => theme.transition.duration} ease;
    /* Not 0: a positioned element at z-index 0 still paints above the card's
       non-positioned children, tinting them. */
    z-index: -1;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

/** Soft-border panel — bento cells, the engagement demo stage. */
export const panelSurface = css`
  background: ${darkHomeTokens.colors.panelFill};
  border: 1px solid ${darkHomeTokens.colors.panelBorder};
  border-radius: ${({ theme }) => theme.spacing[1.5]};
`;

/* The stand-up entrance shared by the hero's VideoStage and GlowSection media:
   lying back on a rotateX tilt, standing to full size on a view() timeline. */
export const standUp = keyframes`
  from {
    transform: rotateX(16deg) scale(0.92);
  }
`;
